import '@xyflow/react/dist/style.css';

import {
  Button,
  Container,
} from '@cloudscape-design/components';
import { PartTypes } from '@parallel-fluidics/shared/constants';
import { getPPartId } from '@parallel-fluidics/shared/models/projects/parts';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react';
import { useTheme } from 'features/theme/themeProvider';
import {
  deleteDoc,
  doc,
  getFirestore,
  setDoc,
} from 'firebase/firestore';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { generateNode, getLayoutedElements } from 'utils/reactFlowUtils';

import { usePPartData } from './pPartProvider';
import ProductNode from './productNode';

const nodeTypes = {
  product: ProductNode,
};

export default function ProjectPartsFlow() {
  const { isDarkMode } = useTheme();
  const { fitView } = useReactFlow();
  const { pParts, savedEdges, projectDocId } = usePPartData();
  const hasFormattedRef = useRef(false);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const handleFormat = useCallback((nodesArr, edgesArr) => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodesArr, edgesArr);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setTimeout(() => {
      fitView({ padding: 0.2 });
    }, 10);
  }, [fitView]);

  // Load edges from savedEdges
  useEffect(() => {
    setEdges(savedEdges || []);
  }, [savedEdges]);

  // Load nodes from pParts, auto formatting each time pParts and edges change
  useEffect(() => {
    if (pParts.length === 0) return;
    // make a copy and sort to avoid mutating the original pParts array: sort by partId ascending
    const sortedPParts = [...pParts].sort((a, b) => a.partId - b.partId);
    const nodesData = sortedPParts.reduce((acc, part) => {
      if (part.type !== PartTypes.PRODUCT) return acc;
      const newNode = generateNode({
        id: part.id,
        data: {
          ...part,
          label: getPPartId(part),
          name: part.name,
        },
      });
      acc.push(newNode);
      return acc;
    }, []);

    const { nodes: layoutedNodes } = getLayoutedElements(nodesData, edges);
    // TODO: https://github.com/parallel-fluidics/parallel-fluidics/issues/751
    setNodes(layoutedNodes);
    // call fitView only once after the first layout
    if (!hasFormattedRef.current) {
      setTimeout(() => {
        fitView({ padding: 0.2 });
      }, 10);
      hasFormattedRef.current = true;
    }
  }, [pParts, edges, fitView]);

  // === Handle React Flow changes ===
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
    changes.forEach((change) => {
      if (change.type === 'remove') {
        deleteDoc(doc(getFirestore(), 'projects', projectDocId, 'edges', change.id)).catch((error) => {
          // eslint-disable-next-line no-console
          console.error(`Failed to delete edge with ID ${change.id}:`, error);
        });
      }
    });
  }, [projectDocId]);

  const onConnect = useCallback(async (edge) => {
    const newEdge = {
      ...edge,
      id: `edge-${edge.source}-${edge.target}`,
    };
    setEdges((eds) => addEdge(newEdge, eds));
    await setDoc(doc(getFirestore(), 'projects', projectDocId, 'edges', newEdge.id), newEdge);
  }, [projectDocId]);

  const isValidConnection = useCallback(
    (connection) => {
      const alreadyTaken = edges.some(
        (e) => e.target === connection.target,
      );
      return !alreadyTaken;
    },
    [edges],
  );

  return (
    <Container fitHeight disableContentPaddings>
      <div style={{ overflow: 'hidden', height: '100%', borderRadius: '1rem' }}>
        <div
          style={{
            position: 'absolute', zIndex: 1, top: 10, left: 10,
          }}
        >
          <Button onClick={() => handleFormat(nodes, edges)}>Format</Button>
        </div>
        <ReactFlow
          colorMode={isDarkMode ? 'dark' : 'light'}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          fitView
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
    </Container>
  );
}
